import { useState } from 'react'
import { api } from '../api'
import { Icon } from './Icon'

/**
 * A quiet nudge, not a gate.
 *
 * Nothing in the product stops working because an address is unconfirmed —
 * except publishing and pushing a draft, which reach a reader or a reviewer
 * and need somebody real behind them. So this never blocks a screen, only sits
 * above one until it is dismissed or the address is confirmed.
 *
 * The copy is careful not to promise an email arrived: there is no mail
 * provider in every deployment, and `requestEmailVerification` answers `ok`
 * either way — the token is logged server-side regardless. Saying "check your
 * inbox" to somebody whose deployment has no mail configured would read as the
 * product lying to them.
 */
export function VerifyBanner({ onDismiss }: { onDismiss: () => void }) {
  const [busy, setBusy] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const request = async () => {
    setBusy(true)
    setError(null)
    try {
      await api.requestEmailVerification()
      setSent(true)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="verifybanner" role="status">
      <Icon name="info" size={14} className="verifybanner__icon" />
      <span className="verifybanner__text">
        {error ??
          (sent
            ? 'Asked for a link. We sent it if mail is set up here — or ask your admin to confirm it for you.'
            : 'Confirm your email address before you can publish or push a draft.')}
      </span>
      {!sent && (
        <button className="btn btn--ghost btn--sm" disabled={busy} onClick={() => void request()}>
          {busy ? <Icon name="spinner" size={12} /> : null}
          Send a confirmation link
        </button>
      )}
      <button
        className="btn btn--ghost btn--icon btn--sm"
        onClick={onDismiss}
        data-tip="Dismiss"
        aria-label="Dismiss"
      >
        <Icon name="close" size={12} />
      </button>
    </div>
  )
}
